import { Injectable } from '@angular/core';
import { AbstractControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import moment from 'moment';

@Injectable()
export class HelperView {

  static to24HourFormat(time12h?: string | null): string | null {
    if (!time12h || typeof time12h !== "string") {
      return null; // no value → return null
    }

    const trimmed = time12h.trim();

    // Check if already in 24-hour format (HH:mm)
    const is24h = /^([01]?\d|2[0-3]):[0-5]\d$/.test(trimmed);
    if (is24h) {
      return trimmed; // already valid 24-hour format
    }

    const parts = trimmed.split(" ");
    if (parts.length < 2) {
      return null; // invalid format
    }

    const [time, modifier] = parts;
    if (!time || !modifier) return null;

    let [hoursStr, minutesStr = "00"] = time.split(":");
    let hours = Number(hoursStr);
    let minutes = Number(minutesStr);

    if (isNaN(hours) || isNaN(minutes)) {
      return null; // invalid numbers
    }

    const mod = modifier.toUpperCase();
    if (mod === "PM" && hours < 12) {
      hours += 12;
    }
    if (mod === "AM" && hours === 12) {
      hours = 0;
    }

    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}`;
  }

  static iconOrColorRequired(shouldValidateFn: () => boolean): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      if (!shouldValidateFn()) return null;

      const icon = group.get('icon')?.value;
      const color = group.get('colourCode')?.value;

      if (!icon && !color) {
        return { iconOrColorRequired: true };
      }

      return null;
    };
  }

  static breakHoursFormatValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) return null;
      const regex = /^(\d+h)?\s?(\d+m)?$/;
      const valid = regex.test(value.trim());
      return valid ? null : { invalidBreakHoursFormat: true };
    };
  }

  static breakFormatAndCompareValidator(
    breakControlName: string,
    hoursControlName: string
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const formGroup = control.parent;
      if (!formGroup) return null;
  
      const breakControl = formGroup.get(breakControlName);
      const hoursControl = formGroup.get(hoursControlName);
  
      if (!breakControl || !hoursControl) return null;
  
      const breakValue = breakControl.value?.trim();
      const hoursValue = hoursControl.value;
  
      if (!breakValue || !hoursValue) return null;
  
      const breakMinutes = HelperView.parseDurationToMinutes(breakValue);
      const totalEditedMinutes = HelperView.parseDurationToMinutes(hoursValue);
  
      if (totalEditedMinutes < breakMinutes) {
        return { editedLessThanBreak: true };
      }
      else {
        return null
      }
    };
  }

  static parseDurationToMinutes(duration: string): number {
    const regex = /(\d+)\s*(h|m)/gi;
    const matches = duration.matchAll(regex);
  
    let totalMinutes = 0;
  
    for (const match of matches) {
      const value = parseInt(match[1]);
      const unit = match[2].toLowerCase();
  
      if (unit === 'h') totalMinutes += value * 60;
      else if (unit === 'm') totalMinutes += value;
    }
  
    return totalMinutes;
  }

  static timeRangeValidator(
    startKey: string,
    endKey: string,
    dutyType?: string,
    nonCont?: boolean,
    compareKey?: string
  ): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const startControl = group.get(startKey);
      const endControl = group.get(endKey);
      const compareControl = compareKey ? group.get(compareKey) : null;

      if (!startControl || !endControl) return null;

      const toMinutes = (time: string): number => {
        const [h, m] = time?.split(':').map(Number) || [];
        return !isNaN(h) && !isNaN(m) ? h * 60 + m : 0;
      };

      const start = toMinutes(startControl.value);
      const end = toMinutes(endControl.value);
      const compare = compareControl?.value ? toMinutes(compareControl.value) : null;

      // clear previous errors set by this validator
      if (startControl.errors?.['invalidNightSlot'] || startControl.errors?.['invalidRange'] || startControl.errors?.['sameAsFirstSlotEndTime']) {
        startControl.setErrors(null);
      }
      if (endControl.errors?.['invalidNightSlot'] || endControl.errors?.['invalidRange'] || endControl.errors?.['sameAsFirstSlotEndTime']) {
        endControl.setErrors(null);
      }

      if (!start || !end) return null;

      if (dutyType === 'DAY') {
        const DAY_START = 6 * 60;
        const DAY_END = 24 * 60;

        if (start < DAY_START || start > DAY_END || end < DAY_START || end > DAY_END) {
          startControl.setErrors({ invalidDaySlot: true });
          endControl.setErrors({ invalidDaySlot: true });
          return null;
        }

        if (end <= start) {
          endControl.setErrors({ invalidRange: true });
          return null;
        }

        if (nonCont && compare !== null && compare >= end && endKey !== compareKey) {
          endControl.setErrors({ sameAsFirstSlotEndTime: true });
          return null;
        }

      } else if (dutyType === 'NIGHT') {
        const NIGHT_START = 18 * 60;
        const NIGHT_END = 8 * 60;

        const adjust = (t: number) => (t < NIGHT_START ? t + 1440 : t);
        const isBetween = (start: number, end: number, comp: number) =>
          start <= end ? comp >= start && comp <= end : comp >= start || comp <= end;

        const adjStart = start;
        const adjEnd = end;
        const adjCompare = compare !== null ? adjust(compare) : null;

        if (!isBetween(NIGHT_START, NIGHT_END, adjStart) || !isBetween(NIGHT_START, NIGHT_END, adjEnd)) {
          startControl.setErrors({ invalidNightSlot: true });
          endControl.setErrors({ invalidNightSlot: true });
          return null;
        }

        if (adjust(adjEnd) <= adjust(adjStart)) {
          endControl.setErrors({ invalidRange: true });
          return null;
        }

        if (nonCont && adjCompare !== null && adjCompare >= adjust(adjEnd) && endKey !== compareKey) {
          endControl.setErrors({ sameAsFirstSlotEndTime: true });
          return null;
        }
      }

      return null;
    };
  }
  // ////////////////////////////////////////////////////////////////////


  static dayStart = moment('06:00', 'HH:mm'); // 6:00 AM
  static dayEnd = moment('23:59', 'HH:mm'); // 10:00 PM (22:00 in 24-hour format)
  static nightShiftStart =moment('18:00', 'HH:mm');
  static endLimit = moment('08:00', 'HH:mm').add(1, 'day'); // 08:00 next day

  //first slot start time
  static firstSlotStartValidator(
    startTimeKey: string,
    endTimeKey: string,
    dutyType?: string,
    nonCont?: boolean,
    compareKey?: string
  ): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
    const formGroup = control.parent;
    if (!formGroup) return null;
    const startTimeControl = formGroup.get(startTimeKey);
    const endTimeControl = formGroup.get(endTimeKey);
    const compareControl: any = compareKey ? formGroup.get(compareKey) : null;
    if (!startTimeControl || !endTimeControl) return null;
    const firstSlotStartTime = startTimeControl.value;
    const firstSlotEndTime = endTimeControl.value;
    const compareTimeCtrl: any = compareControl?.value;
    //  Detect if input is in 12-hour format and convert to 24-hour format
    const startTime = moment(
      firstSlotStartTime,
      ['hh:mm A', 'HH:mm'],
      true
    ).format('HH:mm');
    const endTime = moment(firstSlotEndTime, ['hh:mm A', 'HH:mm'], true).format(
      'HH:mm'
    );
    const compare: any = moment(
      compareTimeCtrl,
      ['hh:mm A', 'HH:mm'],
      true
    ).format('HH:mm');
    let start = moment(startTime, 'HH:mm');
    let end = moment(endTime, 'HH:mm');
    let compareTime = moment(compare, 'HH:mm');
    if (dutyType === 'DAY') {
     if (start.isValid() && end.isValid()) {
        if (!start.isBetween(HelperView.dayStart, HelperView.dayEnd, undefined, '[]')) {
          return { invalidDaySlot: true };
        }
        else{
          clearErrors(startTimeControl);
        }

        if (!end.isBetween(HelperView.dayStart, HelperView.dayEnd, undefined, '[]')) {
          return { invalidDaySlot: true };
        }
        if (end.isSameOrBefore(start)) {
          return { invalidRange: true };
        }  
        else{
          clearErrors(endTimeControl);
        }     
      } 

       else {
        if (start.isValid()) {
          
          if (!start.isBetween(HelperView.dayStart, HelperView.dayEnd, undefined, '[]')) {
            return { invalidDaySlot: true };
          }
        }
        if (end.isValid()) {
          if (!end.isBetween(HelperView.dayStart, HelperView.dayEnd, undefined, '[]')) {
            return { invalidDaySlot: true };
          }
        }
      }

       if (nonCont === true) {
        if (compareTime.isValid()) {
          if ((compareTime.isSameOrAfter(end)) && (compareKey !== endTimeKey)) {
            return { sameAsFirstSlotEndTime: true };
          }
        }
      } else {
      }
      return null
  }

  else if (dutyType === 'NIGHT') {
        if (
          start.isSameOrAfter(moment('00:00', 'HH:mm')) &&
          start.isBefore(HelperView.nightShiftStart)
        ) {
          start = moment(startTime, 'HH:mm').add(1, 'day');
        }
        if (
          end.isSameOrAfter(moment('00:00', 'HH:mm')) &&
          end.isBefore(HelperView.nightShiftStart)
        ) {
          end = moment(endTime, 'HH:mm').add(1, 'day');
        }
        if (nonCont === true) {
          if (compareTime.isValid()) {
            if (
              compareTime.isSameOrAfter(moment('00:00', 'HH:mm')) &&
              compareTime.isBefore(HelperView.nightShiftStart)
            ) {
              compareTime = moment(startTime, 'HH:mm').add(1, 'day');
            }
            if ((compareTime.isSameOrAfter(end) && compareKey !== endTimeKey)) {
              return { invalidRange: true };
            }
          }
        }
        if (
          start.isSameOrAfter(moment('00:00', 'HH:mm')) &&
          start.isBefore(HelperView.nightShiftStart)
        ) {
          if (start.isAfter(end)) {
            return { invalidRange: true };
          }
        }
        if (
          end.isSameOrAfter(moment('00:00', 'HH:mm')) &&
          end.isBefore(HelperView.nightShiftStart)
        ) {
          if (end.isBefore(start)) {
            return { invalidRange: true };
          }
        }
        if (!(start.isSameOrAfter(HelperView.nightShiftStart) && start.isSameOrBefore(HelperView.endLimit))){
          return { invalidNightSlot: true };
        }
        if((!(end.isSameOrAfter(HelperView.nightShiftStart) && end.isSameOrBefore(HelperView.endLimit)) && endTimeKey === 'firstSlotStartTime')){
          return { invalidNightSlot: true };
        }
        if (end.isSameOrBefore(start)) {
          return { invalidRange: true };
        }
         if(nonCont){
          clearErrors(startTimeControl, endTimeControl);
        }
        return null;
      }
       if(nonCont){
          clearErrors(startTimeControl, endTimeControl);
        }
      return null;
    };
  }
  
  /**
   * @param input string formatted as "dd.MM.yyyy HH:mm:ss"
   * @param mode 'showTodayLabel' | 'showTimeIfToday' (default: 'showTimeIfToday')
   */
  static formatChatDate(input: string, mode: 'showTodayLabel' | 'showTimeIfToday' = 'showTimeIfToday'): string {
    const [datePart, timePart] = input.split(" ");
    const [day, month, year] = datePart.split(".");
    const [hour, minute, second] = timePart.split(":");

    const inputDate = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second)
    );

    const now = new Date();

    const isSameDay = inputDate.toDateString() === now.toDateString();

    const yesterday = new Date();
    yesterday.setDate(now.getDate() - 1);
    const isYesterday = inputDate.toDateString() === yesterday.toDateString();

    const diffInMs = now.getTime() - inputDate.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    const timeString = inputDate.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });

    if (isSameDay) {
      return mode === 'showTodayLabel' ? 'constants.DATE.TODAY' : timeString;
    }

    if (isYesterday) {
      return 'constants.DATE.YESTERDAY';
    }

    if (diffInDays < 7) {
      return inputDate.toLocaleDateString(undefined, { weekday: 'long' });
    }

    const dd = String(inputDate.getDate()).padStart(2, '0');
    const mm = String(inputDate.getMonth() + 1).padStart(2, '0');
    const yyyy = inputDate.getFullYear();
    return `${dd}.${mm}.${yyyy}`;
  }
  // For chat bubble timestamp, we only want to show time in 12-hour format with AM/PM
  static formatChatBubbleTime(input: string): string {
    const [datePart, timePart] = input.split(" ");
    const [day, month, year] = datePart.split(".");
    const [hour, minute, second] = timePart.split(":");

    const inputDate = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second)
    );

    return inputDate.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }

  //second slot end time
  static firstSlotEndValidator(
    startTimeKey: string,
    endTimeKey: string,
    dutyType?: string,
    nonCont?: boolean,
    compareKey?: string
  ): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
    const formGroup = control.parent;
    if (!formGroup) return null;
    const startTimeControl = formGroup.get(startTimeKey);
    const endTimeControl = formGroup.get(endTimeKey);
    const compareControl: any = compareKey ? formGroup.get(compareKey) : null;
    if (!startTimeControl || !endTimeControl) return null;
    const firstSlotStartTime = startTimeControl.value;
    const firstSlotEndTime = endTimeControl.value;
    const compareTimeCtrl: any = compareControl?.value;
    //  Detect if input is in 12-hour format and convert to 24-hour format
    const startTime = moment(
      firstSlotStartTime,
      ['hh:mm A', 'HH:mm'],
      true
    ).format('HH:mm');
    const endTime = moment(firstSlotEndTime, ['hh:mm A', 'HH:mm'], true).format(
      'HH:mm'
    );
    const compare: any = moment(
      compareTimeCtrl,
      ['hh:mm A', 'HH:mm'],
      true
    ).format('HH:mm');
    let start = moment(startTime, 'HH:mm');
    let end = moment(endTime, 'HH:mm');
    let compareTime = moment(compare, 'HH:mm');
    if (dutyType === 'DAY') {
      if (start.isValid() && end.isValid()) {
          if (!start.isBetween(HelperView.dayStart, HelperView.dayEnd, undefined, '[]')) {
            return { invalidDaySlot: true };
          }
          else{
            clearErrors(startTimeControl);
          }

          if (!end.isBetween(HelperView.dayStart, HelperView.dayEnd, undefined, '[]')) {
            return { invalidDaySlot: true };
          }
          if (end.isSameOrBefore(start)) {
            if(endTimeKey === 'secondSlotStartTime'){
              return { sameAsFirstSlotEndTime:true }
            }
            else{
            return { invalidRange: true };
            }
          }  
          else{
            clearErrors(endTimeControl);
          }     
        } 

        else {
          if (start.isValid()) {
            
            if (!start.isBetween(HelperView.dayStart, HelperView.dayEnd, undefined, '[]')) {
              return { invalidDaySlot: true };
            }
          }
          if (end.isValid()) {
            if (!end.isBetween(HelperView.dayStart, HelperView.dayEnd, undefined, '[]')) {
              return { invalidDaySlot: true };
            }
          }
        }

        if (nonCont === true) {
          if (compareTime.isValid()) {
            if ((compareTime.isSameOrAfter(end)) && (compareKey !== endTimeKey)) {
              return { sameAsFirstSlotEndTime: true };
            }
          }
        } else {
        }
        return null
    }

    else if (dutyType === 'NIGHT') {
          if (
            start.isSameOrAfter(moment('00:00', 'HH:mm')) &&
            start.isBefore(HelperView.nightShiftStart)
          ) {
            start = moment(startTime, 'HH:mm').add(1, 'day');
          }
          if (
            end.isSameOrAfter(moment('00:00', 'HH:mm')) &&
            end.isBefore(HelperView.nightShiftStart)
          ) {
            end = moment(endTime, 'HH:mm').add(1, 'day');
          }
          if (nonCont === true) {
            if (compareTime.isValid()) {
              if (
                compareTime.isSameOrAfter(moment('00:00', 'HH:mm')) &&
                compareTime.isBefore(HelperView.nightShiftStart)
              ) {
                compareTime = moment(startTime, 'HH:mm').add(1, 'day');
              }
              if (compareTime.isSameOrAfter(end)) {
                if(endTimeKey === 'secondSlotStartTime'){
                  return { sameAsFirstSlotEndTime: true };
                }
                else{
                  return { invalidRange: true };
                }
              }
            }
          }
          if (
            start.isSameOrAfter(moment('00:00', 'HH:mm')) &&
            start.isBefore(HelperView.nightShiftStart)
          ) {
            if (start.isAfter(end)) {
              return { invalidRange: true };
            }
          }
          if (
            end.isSameOrAfter(moment('00:00', 'HH:mm')) &&
            end.isBefore(HelperView.nightShiftStart)
          ) {
            if (end.isBefore(start)) {
              return { invalidRange: true };
            }
          }
          if (start.isBefore(HelperView.nightShiftStart)) {
            return { invalidNightSlot: true };
          }
          if(nonCont){
            if ((start.isAfter(HelperView.endLimit)) && startTimeKey === 'firstSlotEndTime') {
              return { invalidRange: true };
            }
          }
          else{
            if ((end.isSameOrBefore(start))) {
              return { invalidRange: true };
            }
          }
          if (end.isBefore(HelperView.nightShiftStart)) {
            return { invalidNightSlot: true };
          }
          if (end.isAfter(HelperView.endLimit)) {
            return { invalidNightSlot: true };
          }
          if(nonCont){
            if ((end.isSameOrBefore(start)) && startTimeKey === 'firstSlotEndTime') {
              return { invalidRange: true };
            }
          }
          else{
            if ((end.isSameOrBefore(start))) {
              return { invalidRange: true };
            }
          }
        
          if(nonCont){
            clearErrors(startTimeControl, endTimeControl);
          }
          return null;
        }
        if(nonCont){
            clearErrors(startTimeControl, endTimeControl);
          }
        return null;
      };
  
  }

  //second slot start time
  static secondSlotStartValidator(
    startTimeKey: string,
    endTimeKey: string,
    dutyType?: string,
    nonCont?: boolean,
    compareKey?: string
  ): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
    const formGroup = control.parent;
    if (!formGroup) return null;
    const startTimeControl = formGroup.get(startTimeKey);
    const endTimeControl = formGroup.get(endTimeKey);
    const compareControl: any = compareKey ? formGroup.get(compareKey) : null;
    if (!startTimeControl || !endTimeControl) return null;
    const firstSlotStartTime = startTimeControl.value;
    const firstSlotEndTime = endTimeControl.value;
    const compareTimeCtrl: any = compareControl?.value;
    //  Detect if input is in 12-hour format and convert to 24-hour format
    const startTime = moment(
      firstSlotStartTime,
      ['hh:mm A', 'HH:mm'],
      true
    ).format('HH:mm');
    const endTime = moment(firstSlotEndTime, ['hh:mm A', 'HH:mm'], true).format(
      'HH:mm'
    );
    const compare: any = moment(
      compareTimeCtrl,
      ['hh:mm A', 'HH:mm'],
      true
    ).format('HH:mm');
    let start = moment(startTime, 'HH:mm');
    let end = moment(endTime, 'HH:mm');
    let compareTime = moment(compare, 'HH:mm');
    if (dutyType === 'DAY') {
      if (start.isValid() && end.isValid()) {
          if (!start.isBetween(HelperView.dayStart, HelperView.dayEnd, undefined, '[]')) {
            return { invalidDaySlot: true };
          }
          else{
            clearErrors(startTimeControl);
          }

          if ((!end.isBetween(HelperView.dayStart, HelperView.dayEnd, undefined, '[]')) && endTimeKey === 'secondSlotStartTime') {
            return { invalidDaySlot: true };
          }
          if (end.isSameOrBefore(start)) {
            if(endTimeKey === 'secondSlotStartTime'){
              return { sameAsFirstSlotEndTime:true }
            }
            else{
            return { invalidRange: true };
            }
          }  
          else{
            clearErrors(endTimeControl);
          }     
        } 

        else {
          if (start.isValid()) {
            
            if (!start.isBetween(HelperView.dayStart, HelperView.dayEnd, undefined, '[]')) {
              return { invalidDaySlot: true };
            }
          }
          if (end.isValid()) {
            if (!end.isBetween(HelperView.dayStart, HelperView.dayEnd, undefined, '[]')) {
              return { invalidDaySlot: true };
            }
          }
        }

        if (nonCont === true) {
          if (compareTime.isValid()) {
            if ((compareTime.isSameOrAfter(end)) && (compareKey !== endTimeKey)) {
              return { sameAsFirstSlotEndTime: true };
            }
          }
        } else {
        }
        return null
    }

    else if (dutyType === 'NIGHT') {
          if (
            start.isSameOrAfter(moment('00:00', 'HH:mm')) &&
            start.isBefore(HelperView.nightShiftStart)
          ) {
            start = moment(startTime, 'HH:mm').add(1, 'day');
          }
          if (
            end.isSameOrAfter(moment('00:00', 'HH:mm')) &&
            end.isBefore(HelperView.nightShiftStart)
          ) {
            end = moment(endTime, 'HH:mm').add(1, 'day');
          }
          if (nonCont === true) {
            if (compareTime.isValid()) {
              if (
                compareTime.isSameOrAfter(moment('00:00', 'HH:mm')) &&
                compareTime.isBefore(HelperView.nightShiftStart)
              ) {
                compareTime = moment(startTime, 'HH:mm').add(1, 'day');
              }
              if (compareTime.isSameOrAfter(end)) {
                if(endTimeKey === 'secondSlotStartTime'){
                  return { sameAsFirstSlotEndTime: true };
                }
                else{
                  return { invalidRange: true };
                }
              }
            }
          }
          if (
            start.isSameOrAfter(moment('00:00', 'HH:mm')) &&
            start.isBefore(HelperView.nightShiftStart)
          ) {
            if (start.isAfter(end)) {
              return { invalidRange: true };
            }
          }
          if (
            end.isSameOrAfter(moment('00:00', 'HH:mm')) &&
            end.isBefore(HelperView.nightShiftStart)
          ) {
            if (end.isBefore(start)) {
              return { invalidRange: true };
            }
          }
          if (start.isBefore(HelperView.nightShiftStart)) {
            return { invalidNightSlot: true };
          }
          if (start.isAfter(HelperView.endLimit)) {
            return { invalidNightSlot: true };
          }
          if (end.isBefore(HelperView.nightShiftStart)) {
            return { invalidNightSlot: true };
          }
          if ((end.isAfter(HelperView.endLimit) && endTimeKey === 'secondSlotStartTime')) {
            return { invalidNightSlot: true };
          }
          if (end.isSameOrBefore(start)) {
            return { invalidRange: true };
          }
          // clearErrors(startTimeControl, endTimeControl);
          return null;
        }
        // clearErrors(startTimeControl, endTimeControl);
        return null;
      };
  
  }

  //second slot end time
  static secondSlotEndValidator(
    startTimeKey: string,
    endTimeKey: string,
    dutyType?: string,
    nonCont?: boolean,
    compareKey?: string
  ): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
    const formGroup = control.parent;
    if (!formGroup) return null;
    const startTimeControl = formGroup.get(startTimeKey);
    const endTimeControl = formGroup.get(endTimeKey);
    const compareControl: any = compareKey ? formGroup.get(compareKey) : null;
    if (!startTimeControl || !endTimeControl) return null;
    const firstSlotStartTime = startTimeControl.value;
    const firstSlotEndTime = endTimeControl.value;
    const compareTimeCtrl: any = compareControl?.value;
    //  Detect if input is in 12-hour format and convert to 24-hour format
    const startTime = moment(
      firstSlotStartTime,
      ['hh:mm A', 'HH:mm'],
      true
    ).format('HH:mm');
    const endTime = moment(firstSlotEndTime, ['hh:mm A', 'HH:mm'], true).format(
      'HH:mm'
    );
    const compare: any = moment(
      compareTimeCtrl,
      ['hh:mm A', 'HH:mm'],
      true
    ).format('HH:mm');
    let start = moment(startTime, 'HH:mm');
    let end = moment(endTime, 'HH:mm');
    let compareTime = moment(compare, 'HH:mm');
    if (dutyType === 'DAY') {
      if (start.isValid() && end.isValid()) {
          if (!start.isBetween(HelperView.dayStart, HelperView.dayEnd, undefined, '[]')) {
            return { invalidDaySlot: true };
          }
          else{
            clearErrors(startTimeControl);
          }

          if (!end.isBetween(HelperView.dayStart, HelperView.dayEnd, undefined, '[]')) {
            return { invalidDaySlot: true };
          }
          if (end.isSameOrBefore(start)) {
            if(endTimeKey === 'secondSlotStartTime'){
              return { sameAsFirstSlotEndTime:true }
            }
            else{
            return { invalidRange: true };
            }
          }  
          else{
            clearErrors(endTimeControl);
          }     
        } 

        else {
          if (start.isValid()) {
            
            if (!start.isBetween(HelperView.dayStart, HelperView.dayEnd, undefined, '[]')) {
              return { invalidDaySlot: true };
            }
          }
          if (end.isValid()) {
            if (!end.isBetween(HelperView.dayStart, HelperView.dayEnd, undefined, '[]')) {
              return { invalidDaySlot: true };
            }
          }
        }

        if (nonCont === true) {
          if (compareTime.isValid()) {
            if ((compareTime.isSameOrAfter(end)) && (compareKey !== endTimeKey)) {
              return { sameAsFirstSlotEndTime: true };
            }
          }
        } else {
        }
        return null
    }

    else if (dutyType === 'NIGHT') {
          if (
            start.isSameOrAfter(moment('00:00', 'HH:mm')) &&
            start.isBefore(HelperView.nightShiftStart)
          ) {
            start = moment(startTime, 'HH:mm').add(1, 'day');
          }
          if (
            end.isSameOrAfter(moment('00:00', 'HH:mm')) &&
            end.isBefore(HelperView.nightShiftStart)
          ) {
            end = moment(endTime, 'HH:mm').add(1, 'day');
          }
          if (nonCont === true) {
            if (compareTime.isValid()) {
              if (
                compareTime.isSameOrAfter(moment('00:00', 'HH:mm')) &&
                compareTime.isBefore(HelperView.nightShiftStart)
              ) {
                compareTime = moment(startTime, 'HH:mm').add(1, 'day');
              }
              if (compareTime.isSameOrAfter(end)) {
                if(endTimeKey === 'secondSlotStartTime'){
                  return { sameAsFirstSlotEndTime: true };
                }
                else{
                  return { invalidRange: true };
                }
              }
            }
          }
          if (
            start.isSameOrAfter(moment('00:00', 'HH:mm')) &&
            start.isBefore(HelperView.nightShiftStart)
          ) {
            if (start.isAfter(end)) {
              return { invalidRange: true };
            }
          }
          if (
            end.isSameOrAfter(moment('00:00', 'HH:mm')) &&
            end.isBefore(HelperView.nightShiftStart)
          ) {
            if (end.isBefore(start)) {
              return { invalidRange: true };
            }
          }
          if (start.isBefore(HelperView.nightShiftStart)) {
            return { invalidNightSlot: true };
          }
          if (start.isAfter(HelperView.endLimit)) {
            return { invalidNightSlot: true };
          }
          if (end.isBefore(HelperView.nightShiftStart)) {
            return { invalidNightSlot: true };
          }
          if (end.isAfter(HelperView.endLimit)) {
            return { invalidNightSlot: true };
          }
          if (end.isSameOrBefore(start)) {
            return { invalidRange: true };
          }
          clearErrors(startTimeControl, endTimeControl);
          return null;
        }
        clearErrors(startTimeControl, endTimeControl);
        return null;
      };
  
  }
}

function clearErrors(...controls: AbstractControl[]) {
  controls.forEach((control) => control.setErrors(null));
}

